<template>
  <!-- 国家有值且需要选择城市再开启滑动切换 -->
  <VanTabs
    v-model:active="currentActiveTab"
    :show-header="false"
    :swipeable="
      Boolean(myProps.autoCity && isHaveCity && currentFirstCountryId)
    "
    class="select-location"
    @change="eventChangeTab"
  >
    <VanTab key="country" name="country">
      <div v-show="false">
        {{ countryQuery }} country:{{ country }} city:{{ city }}
      </div>
      <SearchCountry
        key="country"
        v-model="country"
        v-model:query="countryQuery"
        value-key="id"
        :option-list="countryOptionList"
        :multiple="myProps.countryOptions.multiple"
        :placeholder="myProps.countryOptions.placeholder"
        :loading="countryIsLoading"
        :label-key="myProps.labelKey"
        @data="eventCountryGetData"
        @update:model-value="eventUpdateCountry"
        @click:item="eventClickCountry"
      >
        <template #title="props">
          <div class="select-location-country">
            <GovIcon
              class="select-location-country-icon"
              :name="props.option.shortCode.toLowerCase()"
            />
            <span>{{ props.option[myProps.labelKey] }}</span>
          </div>
        </template>
      </SearchCountry>
    </VanTab>
    <VanTab key="city" name="city">
      <SearchCity
        v-model="city"
        v-model:query="cityQuery"
        v-model:check-list="cityCheckList"
        :multiple="myProps.cityOptions.multiple"
        :option-list="cityOptionList"
        :placeholder="myProps.cityOptions.placeholder"
        :loading="cityIsLoading"
        :label-key="myProps.labelKey"
        :country-id="currentFirstCountryId"
        @data="eventCityGetData"
        @update:model-value="eventUpdateCity"
      />
    </VanTab>
  </VanTabs>
</template>

<script setup lang="ts">
  import type {
    CommonParams,
    CountryInterface,
    CityInterface,
  } from '@/api/common'
  import { getCountry, getCity } from '@/api/common'
  import {
    type CountryType,
    type CityType,
    type CountryCityModules,
    COUNTRY_PAGE_SIZE,
    CITY_PAGE_SIZE,
    type OnlyIdData,
  } from './constant'

  const myProps = withDefaults(
    defineProps<{
      // 默认的双向绑定
      modelValue?: {
        country: CountryType
        city: CityType
      }
      // 双向绑定的国家值
      country?: CountryType
      // 双向绑定的城市值
      city?: CityType
      // 当前激活的 tab
      activeTab?: CountryCityModules
      modules?: CountryCityModules[] // 展示的模块
      labelKey?: string // 展示的标签名
      autoCity?: boolean // 点击国家后是否自动切换城市
      // 国家的配置项
      countryOptions?: {
        placeholder: string // 占位文本
        multiple: boolean // 是否多选
      }
      // 城市的配置项
      cityOptions?: {
        placeholder: string // 占位文本
        multiple: boolean // 是否多选
      }
    }>(),
    {
      modelValue: () => ({ country: null, city: null }),
      country: null,
      city: null,
      activeTab: 'country',
      modules: () => ['country', 'city'],
      labelKey: 'nameCn',
      autoCity: false,
      countryOptions: () => ({ placeholder: '', multiple: false }),
      cityOptions: () => ({ placeholder: '', multiple: true }),
    },
  )

  const emit = defineEmits<{
    'update:model-value': [
      {
        country: CountryType
        city: CityType
      },
    ]
    'update:country': [CountryType]
    'update:city': [CityType]
    'update:active-tab': [CountryCityModules]
    'async-data': [] // 使用全量数据同步只有 id 的数据
  }>()

  // 暴露方法
  defineExpose({
    initCallback, // 初始化回调
  })

  // 当前选中的 tab
  const currentActiveTab = ref<CountryCityModules>(myProps.activeTab)

  // 国家
  const country = ref<CountryType>(
    myProps.country || myProps.modelValue.country,
  )
  const countryQuery = ref('')
  const countryOptionList = ref<CountryInterface[]>([])
  const countryIsLoading = ref(false)
  let countryCurrent = 1
  let countryTotal = 0
  let countryPages = 0

  // 城市
  const city = ref<CityType>(myProps.city || myProps.modelValue.city)
  const cityQuery = ref('')
  const cityOptionList = ref<CityInterface[]>([])
  const cityIsLoading = ref(false)
  let cityCurrent = 1
  let cityTotal = 0
  let cityPages = 0

  const cityCheckList = ref<any>([])
  const { isCn } = useVipLogo()

  const isHaveCountry = computed(() => myProps.modules.includes('country'))

  // 国家是一个数组
  const isCountryArray = computed(
    () =>
      myProps.countryOptions.multiple &&
      (Array.isArray(myProps.modelValue.country) ||
        Array.isArray(myProps.country)),
  )

  // 当前国家的 id 多选的话使用下标第一个国家的值
  const currentFirstCountryId = computed(() => {
    if (isCountryArray.value) {
      const countryList: CountryInterface[] =
        country.value as CountryInterface[]
      return countryList && countryList.length > 0 ? countryList[0]!.id : ''
    } else {
      return (country.value as CountryInterface)?.id || ''
    }
  })

  const isHaveCity = computed(() => myProps.modules.includes('city')) // 是否有城市选项
  // 是否只有城市选项
  const isOnlyHaveCity = computed(
    () => isHaveCity.value && myProps.modules.length === 1,
  )

  const isCityArray = computed(
    () =>
      myProps.cityOptions.multiple &&
      (Array.isArray(myProps.city) || Array.isArray(myProps.modelValue.city)),
  ) // 城市是一个数组

  // 触发事件传递数据
  function emitModelValue() {
    emit('update:model-value', {
      country: country.value,
      city: city.value,
    })
  }

  function eventClickCountry() {
    if (isHaveCity.value && myProps.autoCity) {
      // 切换城市
      currentActiveTab.value = 'city'
      eventChangeTab()
    }
  }
  // 国家的值更新
  function eventUpdateCountry() {
    if (isHaveCity.value && city.value?.countryId !== country.value?.id) {
      eventClickCountry()
      // 清空城市的选择和配置
      city.value = null
      cityQuery.value = ''
      resetCity()
      eventCityGetData(false)
    }

    emit('update:city', city.value) // 清空城市的值
    emit('update:country', country.value)
    emitModelValue()
  }
  // 重置国家数据
  function resetCountry() {
    countryCurrent = 1
    countryTotal = 0
    countryPages = 0
    countryOptionList.value = []
  }
  // 加载国家数据
  function eventCountryGetData(isLoadMore: boolean) {
    if (!isLoadMore) {
      resetCountry()
      getCountryList()
    } else if (countryCurrent < countryPages) {
      countryCurrent += 1
      getCountryList()
    }
  }
  // 获取国家数据
  async function getCountryList(idList: number[] = []) {
    try {
      if (!countryIsLoading.value) {
        countryIsLoading.value = true
        const params: CommonParams = {
          sort: 'nameEn',
          current: countryCurrent,
          prefixSearchContent: countryQuery.value,
          size: COUNTRY_PAGE_SIZE,
        }

        if (idList.length > 0) {
          params.appointSearch = [
            {
              name: 'countryId',
              val: JSON.stringify(idList),
            },
          ]
        }
        const data = await getCountry(params)

        countryTotal = data.total
        countryPages = data.pages

        if (countryOptionList.value.length < countryTotal) {
          countryOptionList.value.push(...data.records)
        }
      }
    } catch (e) {
      console.error(e)
      countryOptionList.value = []
    } finally {
      countryIsLoading.value = false
    }
  }

  // 重置城市数据
  function resetCity() {
    cityCurrent = 1
    cityTotal = 0
    cityPages = 0
    cityOptionList.value = []
  }
  // 获取城市数据
  async function getCityList(idList: (string | number)[] = []) {
    try {
      if (!cityIsLoading.value) {
        cityIsLoading.value = true

        const params: CommonParams = {
          sort: 'nameEn',
          current: cityCurrent,
          prefixSearchContent: cityQuery.value,
          size: CITY_PAGE_SIZE,
        }

        if (currentFirstCountryId.value) {
          // city 的数据需要 country 的值
          params.appointSearch = [
            {
              name: 'allCityByCountryId',
              val: String(currentFirstCountryId.value),
            },
          ]
        }

        if (idList.length > 0) {
          const data = {
            name: 'cityId' as const,
            val: JSON.stringify(idList),
          }

          if (params.appointSearch) {
            params.appointSearch.push(data)
          } else {
            params.appointSearch = [data]
          }
        }

        const result = await getCity(params)

        cityTotal = result.total
        cityPages = result.pages
        if (cityOptionList.value.length < cityTotal) {
          cityOptionList.value.push(...result.records)
        }
      }
    } catch (e) {
      console.error(e)
      cityOptionList.value = []
    } finally {
      cityIsLoading.value = false
    }
  }

  // 当前是否已选有效国家
  const hasValidCountry = () =>
    Boolean((country.value as CountryInterface | null)?.id)

  // 按城市所属国家回填国家（不复用分页列表 append，避免漏写）
  async function backfillCountryFromCity(selectedCity: CityInterface) {
    if (!selectedCity.countryId) return
    if (
      hasValidCountry() &&
      (country.value as CountryInterface).id === selectedCity.countryId
    ) {
      return
    }

    try {
      const data = await getCountry({
        sort: 'nameEn',
        current: 1,
        size: 1,
        appointSearch: [
          {
            name: 'countryId',
            val: JSON.stringify([selectedCity.countryId]),
          },
        ],
      })
      const countryData = data.records?.[0]
      if (!countryData) return

      country.value = countryData
      if (!countryOptionList.value.some((item) => item.id === countryData.id)) {
        countryOptionList.value.unshift(countryData)
      }
      countryQuery.value =
        (countryData[myProps.labelKey as keyof CountryInterface] as string) ||
        ''
      // 仅同步国家值；城市已先 emit，避免弹层卸载后丢失
      emit('update:country', country.value)
      emitModelValue()
      emit('async-data')
    } catch (e) {
      console.error(e)
    }
  }

  // 城市数据更新：先同步城市（弹层可能马上卸载），再异步回填国家
  function eventUpdateCity() {
    const selectedCity = city.value as CityInterface | null
    emit('update:city', city.value)
    emitModelValue()
    if (selectedCity?.countryId) {
      void backfillCountryFromCity(selectedCity)
    }
  }

  // 加载城市数据
  function eventCityGetData(isLoadMore: boolean) {
    if (!isLoadMore) {
      resetCity()
      if (cityOptionList.value.length === 0) {
        getCityList()
      }
    } else if (cityCurrent < cityPages) {
      cityCurrent += 1
      getCityList()
    }
  }

  // tab 切换
  function eventChangeTab() {
    emit('update:active-tab', currentActiveTab.value)
  }

  // 初始化回调 暴露给外面
  async function initCallback() {
    if (isOnlyHaveCity.value) {
      currentActiveTab.value = 'city'
      eventChangeTab()
    }

    const promiseList: Promise<void>[] = []

    let isCountryModelValue = false // 是否是从 modelValue 中加载数据
    let isCityModelValue = false // 是否是从 modelValue 中加载数据

    // 判断是不是数组格式
    let countryParams: OnlyIdData[] = []
    if (isCountryArray.value) {
      countryParams =
        country.value && (country.value as CountryInterface[]).length > 0
          ? (country.value as CountryInterface[])
          : []
    } else {
      countryParams = country.value ? [country.value as CountryInterface] : []
    }

    if (countryParams.length > 0) {
      isCountryModelValue = true
      try {
        promiseList.push(getCountryList(countryParams.map((item) => item.id)))
      } catch (e) {
        console.error(e)
      }
    }

    let cityParams: OnlyIdData[] = []
    if (isCityArray.value) {
      cityParams =
        city.value && (city.value as CityInterface[]).length > 0
          ? (city.value as CityInterface[])
          : []
    } else {
      cityParams = city.value ? [city.value as CityInterface] : []
    }
    if (cityParams.length > 0) {
      isCityModelValue = true
      try {
        promiseList.push(getCityList(cityParams.map((item) => item.id)))
      } catch (e) {
        console.error(e)
      }
    }

    try {
      await Promise.all(promiseList)
    } catch (e) {
      console.error(e)
    }

    let isUpdateCountry = false
    let isUpdateCity = false

    // 从 modelValue 中只有 id 的数据同步成全量数据
    if (isCountryModelValue && country.value) {
      // 从 modelValue 中加载的数据 替换掉只有 id 的数据
      const currentCountry = countryOptionList.value.find(
        (item) => item.id === (country.value as CountryInterface).id,
      )
      if (currentCountry) {
        isUpdateCountry = true
        country.value = currentCountry
        countryQuery.value =
          (currentCountry[
            myProps.labelKey as keyof CountryInterface
          ] as string) || ''
      }
    }

    if (isCityModelValue && city.value) {
      // 从 modelValue 中加载的数据 替换掉只有 id 的数据

      if (isCityArray.value && (city.value as []).length > 0) {
        // 数组且有值
        const cityIdList = (city.value as OnlyIdData[]).map((item) => item.id)

        city.value = cityOptionList.value.filter((item) => {
          return cityIdList.includes(item.id)
        })
        // 多选不设置 city query
        isUpdateCity = true
      } else if ((city.value as OnlyIdData).id) {
        // 只是一个对象
        const currentCity = cityOptionList.value.find(
          (item) => item.id === (city.value as CityInterface).id,
        )
        if (currentCity) {
          isUpdateCity = true
          city.value = currentCity
          cityQuery.value =
            (currentCity[myProps.labelKey as keyof CityInterface] as string) ||
            ''
        }
      }
    }

    if (isUpdateCity || isUpdateCountry) {
      if (isUpdateCountry) {
        emit('update:country', country.value)
      }
      if (isUpdateCity) {
        emit('update:city', city.value)
      }

      emitModelValue()
      emit('async-data')
    }
  }

  watch(
    () => myProps.activeTab,
    (nv) => (currentActiveTab.value = nv),
  )
  watch(
    () => myProps.modelValue,
    (nv) => {
      const current = nv || {}
      country.value = current.country || null
      city.value = current.city || null
    },
  )
  watch(
    () => myProps.country,
    (nv: any) => {
      if (!nv) {
        countryQuery.value = ''
      } else {
        countryQuery.value = isCn.value ? nv.nameCn : nv.nameEn
      }
      country.value = nv || null
    },
  )
  watch(
    () => myProps.city,
    (nv) => {
      if (!nv) {
        cityQuery.value = ''
      }
      city.value = nv || null
    },
  )
</script>
<style scoped lang="scss">
  .select-location {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;

    :deep(.van-tabs) {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }

    :deep(.van-tabs__content),
    :deep(.van-tabs__track) {
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
    }

    :deep(.van-tab__panel) {
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .select-location-country {
      display: flex;
      align-items: center;
      justify-content: flex-start;

      .select-location-country-icon {
        width: 16px;
        height: 12px;
        margin-right: 10px;
        border-radius: 2px;
      }
    }
  }
</style>
